<script>
	// NPM Modules
	import { navigate } from 'svelte-navaid';
	import dayjs from 'dayjs';
	import Link from 'svelte-navaid/Link.svelte';
	// Stores
	import SportStore from '@/Stores/Sport/SportStore';
	import SportEventStore from '@/Stores/Sport/SportEventStore';
	import CompetitionStore from '@/Stores/Sport/CompetitionStore';
	// Components
	import Datepicker from '@/Components/Datepicker.svelte';
	import CheckBox from '@/Components/CheckBox.svelte';
	import Pagination from '@/Components/Pagination.svelte';
	import PillMultiSelect from '@/Components/PillMultiSelect.svelte';
	// Services
	import { getUrlParameter, paginationBuilder, setUrlParameter, routeForSportEvent } from '@/Services/Helpers';
	// Partials
	import Status from '@/Partials/Sport/Event/_Status.svelte';
	// Constants
	import { EventStatus } from '@/Constants/Event';

	export let sport;

	export function refreshSportEvents() {
		getSportEvents();
	}

	let onlyShowManualEventsValue = [true];
	let onlyShowManualEvents = true;

	$: filter = {
		sportId: sport.id,
		compId: false,
		status: false,
		fromDate: dayjs().subtract(2, 'month').hour(0).minute(0).second(0).format('YYYY-MM-DDTHH:mm:ssZ'),
		toDate: dayjs().add(2, 'month').hour(23).minute(59).second(59).format('YYYY-MM-DDTHH:mm:ssZ')
	};
	$: startDate = dayjs().subtract(2, 'month').format('YYYY-MM-DD');
	$: endDate = dayjs().add(2, 'month').format('YYYY-MM-DD');
	$: compId = false;
	$: selectedCompId = [false];
	$: selectedSportsCompetitions =
		($CompetitionStore &&
			$CompetitionStore
				.sortBy('name')
				.map((comp) => {
					return [comp.id, comp.name];
				})
				.all()) ??
		[]; // Get the currently selected sports competitions
	$: eventStatuses = mapEventStatuses();
	$: selectedEventStatuses = [];
	$: (compId || !compId) &&
		(onlyShowManualEvents || !onlyShowManualEvents) &&
		startDate &&
		endDate &&
		selectedEventStatuses &&
		filterSportEvents(); // When filter selectors change fetch new sport events

	$: links = $SportEventStore && $SportEventStore.count() && paginationBuilder(SportEventStore.getPagination()); //Used for pagination component
	$: sportEvents = ($SportEventStore && $SportEventStore.all()) ?? [];

	$: page = parseInt(getUrlParameter('page')) || 1; // Default to page 1!
	$: page && page !== 1 && setUrlParameter('page', page);

	$: sport && page && filter && $SportStore && getSportEvents();

	CompetitionStore.getCompetitionsForSport(sport.id);

	function changePage(e) {
		if (e) {
			page = e.detail.page;
			setUrlParameter('page', page);
		}
	}

	function changeCompetition(comp_id) {
		if (typeof parseInt(comp_id) !== 'undefined' && comp_id !== false) {
			compId = comp_id;
		} else {
			compId = false;
		}
		selectedCompId = [compId];
	}

	function mapEventStatuses() {
		let eventStatusesTemp = [];
		for (const [value, key] of Object.entries(EventStatus)) {
			eventStatusesTemp.push([key, value]);
		}
		return eventStatusesTemp;
	}

	function filterSportEvents() {
		filter = {
			sportId: sport.id,
			compId: compId,
			status: selectedEventStatuses,
			fromDate: dayjs(startDate).hour(0).minute(0).second(0).format('YYYY-MM-DDTHH:mm:ssZ'),
			toDate: dayjs(endDate).hour(23).minute(59).second(59).format('YYYY-MM-DDTHH:mm:ssZ'),
			manual: onlyShowManualEvents
		};
	}

	function getSportEvents() {
		SportEventStore.getSportEvents(filter, true, { size: 50, number: page }, false, true);
	}
</script>

<div class="flex mt-1">
	<div class="flex items-center space-x-2 text-sm">
		<!-- START DATE -->
		<Datepicker
			selected={startDate}
			displayFormat="DD-MM-YYYY"
			dateChosen={(date) => {
				startDate = dayjs(date).format('YYYY-MM-DD');
			}}
		/>
		<!-- END DATE -->
		<Datepicker
			selected={endDate}
			displayFormat="DD-MM-YYYY"
			dateChosen={(date) => {
				endDate = dayjs(date).format('YYYY-MM-DD');
			}}
		/>
		<PillMultiSelect
			className="min-w-24"
			name="competition"
			label="Competition"
			singleSelection={true}
			options={Array.isArray(selectedSportsCompetitions) && selectedSportsCompetitions.length > 0
				? [[false, 'All Competitions'], ...selectedSportsCompetitions]
				: [[false, 'All Competitions']]}
			action={false}
			checked={selectedCompId}
			on:change={(change) => changeCompetition(change.detail.checked[0])}
		/>
		<PillMultiSelect
			className="min-w-24"
			name="status"
			label="Event status"
			options={eventStatuses}
			action={false}
			bind:checked={selectedEventStatuses}
		/>
		<PillMultiSelect
			className="min-w-24"
			name="manual"
			label="Manual events"
			singleSelection={true}
			options={[
				[false, 'Show all events'],
				[true, 'Only show manual events']
			]}
			action={false}
			checked={onlyShowManualEventsValue}
			on:change={(event) => (onlyShowManualEvents = event.detail.checked[0])}
		/>
	</div>
</div>
<Pagination {links} on:click={changePage} className="my-3" />
<table class="w-full whitespace-nowrap mt-3">
	<thead>
		<tr class="text-xs text-left text-gray-500 uppercase">
			<th class="w-px px-6 pt-3 pb-2 font-medium">Fixture</th>
			<th class="px-6 pt-3 pb-2 font-medium" />
			<th class="w-px px-6 pt-3 pb-2 font-medium text-left">Competition</th>
			<th class="w-px px-6 pt-3 pb-2 font-medium text-left">Round</th>
			<th class="w-px px-6 pt-3 pb-2 font-medium text-center">Status</th>
			<th class="w-px px-6 pt-3 pb-2 font-medium text-left">Start Time</th>
			<th class="w-px px-6 pt-3 pb-2 font-medium text-center">Outright</th>
			<th class="w-px px-6 pt-3 pb-2 font-medium text-center">Sport</th>
			<th class="w-px px-6 pt-3 pb-2 font-medium text-center">Manual</th>
			<th class="w-px px-6 pt-3 pb-2 font-medium text-center" />
		</tr>
	</thead>
	<tbody class="bg-white">
		{#each sportEvents as sportEvent (sportEvent.id)}
			<tr class="cursor-pointer hover:bg-gray-100 focus-within:bg-gray-100">
				<td class="w-px p-4 px-6 border-t">
					<div class="flex items-center">
						<div class="mr-2 font-semibold">
							<Link href={routeForSportEvent(sportEvent)} class="focus:outline-none hover:underline">
								{sportEvent.name}
							</Link>
						</div>
					</div>
				</td>
				<td class="border-t" />
				<td class="w-px px-6 border-t">
					{sportEvent.competition.name}
				</td>
				<td class="w-px px-6 border-t">
					{sportEvent.round ?? ''}
				</td>
				<td class="w-px px-6 border-t text-right">
					<Status event={sportEvent} readOnly={true} />
				</td>
				<td class="w-px px-6 border-t">
					{dayjs(sportEvent.start_time).format('D MMM YYYY, HH:mm')}
				</td>
				<td class="w-px px-6 border-t">
					<CheckBox disabled center={true} bind:checked={sportEvent.outright} />
				</td>
				<td class="w-px px-6 border-t text-right">
					<div class="px-2 py-1 bg-green-500 rounded-md text-light">
						{sport.name}
					</div>
				</td>
				<td class="w-px px-6 border-t">
					{#if sportEvent.is_manual}
						<div class="px-2 py-1 bg-orange-500 rounded-md text-light">MANUAL</div>
					{/if}
				</td>
				<td class="w-px px-6 border-t">
					<button
						on:click={() => navigate(routeForSportEvent(sportEvent))}
						class="flex flex-grow items-center justify-center btn btn-primary-action"
					>
						<span>Go to fixture</span>
					</button>
				</td>
			</tr>
		{:else}
			<tr>
				<td class="px-6 py-4 text-center border-t" colspan="10">
					No events found for {sport.name}.
				</td>
			</tr>
		{/each}
	</tbody>
</table>
<Pagination {links} on:click={changePage} className="my-3" />
